import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { API, graphqlOperation } from "aws-amplify";
import { areaByActiveBySortKey } from "../../../graphql/reloadyard/custom-queries";

import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";

import {
  AutocompleteInput,
  ArrayField,
  ArrayInput,
  Button,
  Create,
  Datagrid,
  FormDataConsumer,
  FormTab,
  FunctionField,
  ListContextProvider,
  minValue,
  number,
  NumberField,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  useList,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { getInboundShipment } from "../../../graphql/inbound/custom-queries";
import {
  createInventorie,
  createWorkOrderItem,
  updateInventorie,
} from "../../../graphql/mutations";
import { BackButton } from "../../../components/common/BackButton";
import {
  CustomFieldsButton,
  CustomFieldsTallySheet,
  getCustomFieldsToSave,
} from "../../../components/common/CustomFields";
import { NewBadge } from "../../../components/common/FeatureBadges";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { SmartTipIcon } from "../../../components/common/SmartTipIcon";
import CustomizableDatagrid from "../../../components/common/ra-customizable-datagrid";
import {
  CopyDownIcon,
  RemoveIcon,
} from "../../../components/common/iconography";
import {
  AclType,
  InventoryField,
  InventoryStatus,
  NoteType,
  WorkOrderState,
  WorkOrderSteps,
} from "../../../utils/constants";
import {
  getCommodityUnits,
  getUnitName,
  getWeightUOM,
  getYardCommodity,
  isBulkCommodity,
  isInventoryFieldVisible,
  unitOptionRenderer,
} from "../../../utils/loadTypeUtils";
import {
  isAuthorized,
  logError,
  setupLocationMaps,
} from "../../../utils/appUtils";
import { filterAreas, filterZones } from "../../../utils/locationUtils";
import {
  deepRedirect,
  getBackPath,
  normalizeValue,
} from "../../../utils/utils";
import {
  fetchInboundBillingRules,
  filterApplicableManualRules,
  getManualRuleLabel,
} from "../accounts/chargeRules/ChargeRuleUtils";
import { ChargeEvent } from "../accounts/chargeRules/chargeRuleConstants";
import { showWeight } from "../inventory/InventoryUitls";
import {
  AddCatalogUploadButtons,
  applyManualRuleDefaultQuantity,
  calcNetWeight,
  calcTotalQuantity,
  getAutoCharges,
  getInfoBubble,
  getManualChargeAmount,
  handleInventoriesQty,
  handleInventoriesWeight,
  handleUnitChange,
} from "../inboundShipments/InboundUtils";
import { WorkAside } from "./WorkAside";

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
  arrayInput: {
    marginTop: theme.spacing(0),
    width: "100%",
    "& > div": {
      width: "100%",
    },
    "& .RaSimpleFormIterator-root": {
      width: "100%",
    },
    "& .RaSimpleFormIterator-line": {
      width: "100%",
    },
    "& .RaSimpleFormIterator-form": {
      flex: 1,
      width: "100%",
    },
    "& .ra-input": {
      width: "100%",
    },
  },
  arrayInputHeader: {
    display: "flex",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    justifyContent: "space-between",
  },
  tallySheetRow: {
    marginBottom: theme.spacing(1),
    width: "100%",
  },
  formGrid: {
    width: "100%",
  },
  inputInventoryCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    width: "100%",
    backgroundColor: theme.palette.background.paper,
  },
  inputInventoryMeta: {
    marginTop: theme.spacing(1),
  },
  inputInventoryItemRow: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5),
    width: "100%",
    backgroundColor: theme.palette.action.hover,
  },
  inputInventoryItemExpand: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
}));

const validateRequired = required();
const validateQuantity = [required(), number(), minValue(0)];
const validatePieces = [number(), minValue(1)];

const getLoadTypeLabel = (reloadYardCommodityId) => {
  const yardCommodity = getYardCommodity(reloadYardCommodityId);
  if (!yardCommodity) return "";
  return `${yardCommodity.commodity?.name || ""} - ${yardCommodity.units || ""}`;
};

const isPersistedOutputInventory = (row) =>
  row?.id && row?.status && row.status !== InventoryStatus.NEW;

const getReloadYardCommodityId = (row, fallbackCommodityId) =>
  row?.productId && row?.reloadYardCommodityId
    ? row.reloadYardCommodityId
    : fallbackCommodityId;

// Output tally: Inventory only (NEW, no location, no InventoryItem).
const buildOutputInventoryInput = (
  workOrderId,
  inbound,
  tallyRow,
  index,
  values,
  defaultReloadYardCommodityId,
) => {
  const quantityAdvised = Number(
    tallyRow.quantityAdvised ?? tallyRow.quantityToProcess ?? 0,
  );
  const reloadYardCommodityId = getReloadYardCommodityId(
    tallyRow,
    values?.reloadYardCommodityId || defaultReloadYardCommodityId,
  );
  const input = {
    inboundShipmentId: inbound.id,
    reloadYardId: inbound.reloadYardId,
    accountId: inbound.accountId,
    accountName: inbound.accountName,
    groups: inbound.groups,
    active: inbound.active || "true",
    areaId: values?.areaId || " ",
    zoneId: values?.zoneId || " ",
    poNumber: tallyRow.poNumber || inbound.poNumber,
    productName: tallyRow.productName,
    quantityAdvised,
    quantityCommitted: 0,
    pcs: tallyRow.pcs,
    pcsHeight: tallyRow.pcsHeight,
    pcsWidth: tallyRow.pcsWidth,
    pcsLength: tallyRow.pcsLength,
    inventoryUOM: tallyRow.inventoryUOM || values?.totalUOM || inbound.totalUOM,
    weight: tallyRow.weight,
    weightUOM: tallyRow.weightUOM,
    sortKey: index + 10001,
    productCode: tallyRow.productCode ? tallyRow.productCode : null,
    productId: tallyRow.productId ? tallyRow.productId : "",
    reloadYardCommodityId,
    lotType: inbound.lotType ? inbound.lotType : "",
    lotNumber: inbound.lotType ? inbound.lotNumber : "",
    normalizedPoNumber: normalizeValue(inbound.poNumber),
    normalizedRailCarNumber: normalizeValue(inbound.railCarNumber),
    history: [
      {
        text: "Created output inventory for work order.",
        type: NoteType.GENERIC,
        date: new Date(),
        by: window.appUser?.fullName,
      },
    ],
    status: InventoryStatus.NEW,
  };

  if (workOrderId) {
    input.workOrderId = workOrderId;
  }

  input.customFields = getCustomFieldsToSave(
    tallyRow.customFields,
    inbound.account?.inventoryCustomFields,
  );

  return input;
};

const createOutputInventory = async (
  workOrderId,
  inbound,
  tallyRow,
  index,
  values,
  defaultReloadYardCommodityId,
) => {
  const response = await API.graphql(
    graphqlOperation(createInventorie, {
      input: buildOutputInventoryInput(
        workOrderId,
        inbound,
        tallyRow,
        index,
        values,
        defaultReloadYardCommodityId,
      ),
    }),
  );
  return response.data.createInventorie;
};

const updateOutputInventoryWorkOrderId = (inventoryId, workOrderId) =>
  API.graphql(
    graphqlOperation(updateInventorie, {
      input: {
        id: inventoryId,
        workOrderId,
      },
    }),
  );

const buildWorkOrderInput = (
  inbound,
  primaryInputItem,
  values = {},
  inputInventoryId,
) => {
  const now = new Date().toISOString();
  const history = inbound.history ? [...inbound.history] : [];

  history.push({
    type: NoteType.GENERIC,
    date: now,
    by: window.appUser?.fullName,
    text: `Work order created from inbound ${inbound.poNumber}`,
  });

  return {
    owner: inbound.owner,
    groups: inbound.groups,
    active: inbound.active || "true",
    status: inbound.status,
    // Crew list uses workOrderByState with READY / IN_PROGRESS only. Do not
    // copy inbound.state (often COMPLETED or PROCESS_*) or the row never
    // appears in /processWorkOrders.
    state: WorkOrderState.READY,
    workType: "INBOUND",
    poNumber: inbound.poNumber || " ",
    normalizedPoNumber:
      inbound.normalizedPoNumber || normalizeValue(inbound.poNumber),
    totalUOM: inbound.totalUOM,
    skipInvoice: inbound.skipInvoice,
    createDateTime: now,
    startDateTime: null,
    endDateTime: null,
    attachments: inbound.attachments,
    assignedTo: values.assignedTo || inbound.assignedTo,
    workflowType: inbound.workflowType,
    processedBy: null,
    verificationRequired: inbound.verificationRequired,
    hasException: inbound.hasException,
    brokenSeal: inbound.brokenSeal,
    brokenSealNote: inbound.brokenSealNote,
    otherException: inbound.otherException,
    otherExceptionNote: inbound.otherExceptionNote,
    instructions: inbound.instructions,
    notes: inbound.notes,
    history,
    accountName: inbound.accountName,
    accountId: inbound.accountId,
    billToAccountId: inbound.billToAccountId || inbound.accountId,
    reloadYardId: inbound.reloadYardId,
    inputInventoryId,
    areaId:
      values?.areaId ||
      inbound.areaId ||
      primaryInputItem?.areaId ||
      primaryInputItem?.inventory?.areaId,
    zoneId:
      values?.zoneId ||
      inbound.zoneId ||
      primaryInputItem?.zoneId ||
      primaryInputItem?.inventory?.zoneId,
    reloadYardCommodityId:
      values?.reloadYardCommodityId ||
      inbound.reloadYardCommodityId ||
      primaryInputItem?.inventory?.reloadYardCommodityId,
    customFields: inbound.customFields,
    selectedCustomFields: inbound.selectedCustomFields,
    stepName: WorkOrderSteps[0],
  };
};

// Existing input stock: WorkOrderItem references only (no new Inventory).
const buildWorkOrderItemInput = (workOrder, inputRow, sortKey) => ({
  groups: workOrder.groups,
  active: "true",
  status: workOrder.status,
  workOrderId: workOrder.id,
  sortKey,
  poNumber: inputRow.poNumber || workOrder.poNumber,
  quantityToProcess: Number(inputRow.quantityToProcess || 0),
  units:
    inputRow.units || inputRow.inventory?.inventoryUOM || workOrder.totalUOM,
  history: [
    {
      type: NoteType.GENERIC,
      date: new Date().toISOString(),
      by: window.appUser?.fullName,
      text: `Work order item references input inventory item at ${
        inputRow.locationFullName || inputRow.inputInventoryItemId
      }`,
    },
  ],
  accountId: workOrder.accountId,
  reloadYardId: workOrder.reloadYardId,
  inputInventoryId: inputRow.inputInventoryId,
  inputInventoryItemId: inputRow.inputInventoryItemId,
});

const getDefaultInventoryItem = (inventory) => {
  const item = inventory?.inventoryItems?.items?.find(
    (inventoryItem) =>
      inventoryItem?.status !== InventoryStatus.SHIPPED &&
      inventoryItem?.status !== InventoryStatus.ARCHIVED,
  );

  if (!item) return undefined;

  return item.inventory ? item : { ...item, inventory };
};

const getOutputTallyRows = (values) =>
  (values?.inventories?.items || []).filter(
    (row) => Number(row.quantityAdvised || 0) > 0 && row.productName,
  );

const getInputWorkOrderItemRows = (values, inbound) =>
  (values?.inputInventoryItems?.items || [])
    .map((item) => ({
      ...item,
      inputInventoryId: item.inventoryId || item.inventory?.id,
      inputInventoryItemId: item.id,
      poNumber: item.poNumber || item.inventory?.poNumber || inbound?.poNumber,
      quantityToProcess: Number(item.quantityToProcess || 0),
      units:
        item.inventory?.inventoryUOM || values?.totalUOM || inbound?.totalUOM,
      locationFullName: item.locationFullName,
    }))
    .filter(
      (row) =>
        row.inputInventoryItemId &&
        row.inputInventoryId &&
        Number(row.quantityToProcess || 0) > 0,
    );

const WorkOrderCreateActions = ({ backPath, ...props }) => (
  <TopToolbar>
    <BackButton {...props} backPath={backPath} />
  </TopToolbar>
);

const WorkOrderSaveButton = ({
  inbound,
  inputInventoryItem,
  backPath,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const history = useHistory();
  const [isSaving, setIsSaving] = useState(false);

  const getPrimaryInputItem = (values, rows) =>
    (rows || getInputWorkOrderItemRows(values, inbound))[0] || inputInventoryItem;

  // transform builds only the WorkOrder record; the dataProvider persists it.
  // Output inventory + work order items are created afterwards in onSuccess,
  // so child rows are never written before the parent work order exists and a
  // failed work order create leaves nothing behind.
  const transform = (data) => {
    const primaryInputItem = getPrimaryInputItem(data);
    const inputInventoryId =
      primaryInputItem?.inputInventoryId ||
      primaryInputItem?.inventoryId ||
      primaryInputItem?.inventory?.id;
    return buildWorkOrderInput(inbound, primaryInputItem, data, inputInventoryId);
  };

  const onSuccess = async ({ data: workOrder }) => {
    setIsSaving(true);
    try {
      const values = form.getState().values;
      const outputTallyRows = getOutputTallyRows(values);
      const inputWorkOrderItemRows = getInputWorkOrderItemRows(values, inbound);
      const primaryInputItem = getPrimaryInputItem(
        values,
        inputWorkOrderItemRows,
      );
      const defaultReloadYardCommodityId =
        values?.reloadYardCommodityId ||
        primaryInputItem?.inventory?.reloadYardCommodityId ||
        inbound?.reloadYardCommodityId;

      // Now that the work order exists, create new output inventory linked to
      // it directly, or link already-persisted output inventory to it.
      await Promise.all(
        outputTallyRows.map((row, index) =>
          isPersistedOutputInventory(row)
            ? updateOutputInventoryWorkOrderId(row.id, workOrder.id)
            : createOutputInventory(
                workOrder.id,
                inbound,
                row,
                index,
                values,
                defaultReloadYardCommodityId,
              ),
        ),
      );

      await Promise.all(
        inputWorkOrderItemRows.map((row, index) =>
          API.graphql(
            graphqlOperation(createWorkOrderItem, {
              input: buildWorkOrderItemInput(workOrder, row, index),
            }),
          ),
        ),
      );

      notify("Work Order created successfully", "success");
      deepRedirect(history, backPath);
    } catch (err) {
      logError("workOrders:WorkOrderCreate:onSuccess", err, notify);
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    logError("workOrders:WorkOrderCreate:onFailure", error, notify);
  };

  const handleSubmitWithRedirect = () => {
    const values = form.getState().values;
    const outputTallyRows = getOutputTallyRows(values);
    const inputWorkOrderItemRows = getInputWorkOrderItemRows(values, inbound);
    const primaryInputItem = getPrimaryInputItem(
      values,
      inputWorkOrderItemRows,
    );

    if (
      !inbound ||
      !(primaryInputItem?.inventoryId || primaryInputItem?.inventory?.id) ||
      outputTallyRows.length === 0 ||
      inputWorkOrderItemRows.length === 0
    ) {
      notify(
        "Add at least one input item to process and one output inventory row before saving.",
        "warning",
      );
      return;
    }

    props.handleSubmitWithRedirect();
  };

  return (
    <>
      <Backdrop className={classes.backdrop} open={isSaving}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <SaveButton
        label="Save"
        submitOnEnter={false}
        undoable={false}
        disabled={isSaving}
        saving={isSaving}
        handleSubmitWithRedirect={handleSubmitWithRedirect}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
      />
    </>
  );
};

const WorkOrderCreateToolbar = ({
  inbound,
  inputInventoryItem,
  backPath,
  ...props
}) => (
  <Toolbar {...props}>
    <WorkOrderSaveButton
      inbound={inbound}
      inputInventoryItem={inputInventoryItem}
      backPath={backPath}
    />
  </Toolbar>
);

const RemoveCopyButtons = ({
  form,
  setWeightError,
  yardCommodity,
  ...props
}) => {
  const isXSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const handleCopy = () => {
    const n = props.className.lastIndexOf("-");
    const index = Number(props.className.substring(n + 1));
    const inventoryFieldState = form.getFieldState("inventories.items");
    const data = inventoryFieldState?.value[index];
    const totalUOM = form.getState().values.totalUOM;

    if (inventoryFieldState?.value) {
      const newRow = {
        status: InventoryStatus.NEW,
        productName: data.productName,
        productCode: data.productCode,
        productId: data.productId,
        product: data.product,
        quantityAdvised: data.quantityAdvised,
        quantityReceived: data.quantityReceived,
        inventoryUOM: data.inventoryUOM || totalUOM,
        reloadYardCommodityId: data.reloadYardCommodityId,
        weight: data.weight,
        weightUnits: data.weightUnits,
        weightUOM: data.weightUOM,
        customFields: data.customFields,
      };
      form.change("inventories.items", [...inventoryFieldState.value, newRow]);
      handleInventoriesQty(form);
      handleInventoriesWeight(form, setWeightError);
    }
  };

  const handleRemove = (event) => {
    props.onClick(event);
    handleInventoriesQty(form);
    handleInventoriesWeight(form, setWeightError);
  };

  return (
    <Grid
      container
      direction={isXSmall ? "row" : "column"}
      spacing={1}
      alignItems="flex-end"
    >
      <Grid item xs={2} sm={12}>
        <Button label="Remove" color="default" onClick={handleRemove}>
          <RemoveIcon />
        </Button>
      </Grid>
      {!isBulkCommodity(yardCommodity?.commodity) && (
        <Grid item xs={2} sm={12}>
          <Button
            label="Copy"
            color="default"
            size="small"
            onClick={handleCopy}
          >
            <CopyDownIcon />
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

const TallySheetTab = ({
  backPath,
  account,
  billingAccountId,
  yardCommodity,
  ...props
}) => {
  const form = useForm();
  const classes = useStyles();
  const [weightError, setWeightError] = useState(null);
  const [selectedFields, setSelectedFields] = useState([]);
  const [chargeRules, setChargeRules] = useState([]);
  const syncingFormRef = useRef(false);

  useEffect(() => {
    if (account) setSelectedFields(account.inventoryCustomFields);
    else setSelectedFields([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id]);

  useEffect(() => {
    // Rules follow the effective billing account, the same one the work order
    // writes to billToAccountId. BROKERED accounts keep their rules on the
    // broker, so keying this off account.id bills them nothing.
    const fetchInboundChargeRules = async () => {
      const accountId = billingAccountId || account?.id;
      if (!accountId) {
        setChargeRules([]);
        return;
      }
      try {
        const rules = await fetchInboundBillingRules(accountId);
        setChargeRules(rules);
      } catch (error) {
        logError(
          "workOrders:WorkOrderCreate:TallySheetTab:manualChargeRules",
          error,
          false,
        );
        setChargeRules([]);
      }
    };

    fetchInboundChargeRules();
  }, [billingAccountId, account?.id]);

  useEffect(() => {
    const unsubscribe = form.subscribe(
      ({ values }) => {
        if (syncingFormRef.current) return;
        const formData = values;
        const weightUOM =
          formData?.weightUnits ||
          formData?.scaleWeightUnits ||
          getWeightUOM(account, null);
        const net = calcNetWeight(formData, setWeightError, weightUOM);
        const nextNetWeight = net != null ? net : null;
        const nextTotalQty = calcTotalQuantity(formData);
        const updates = [];
        if ((formData?.netWeight ?? null) !== nextNetWeight) {
          updates.push(["netWeight", nextNetWeight]);
        }
        if (weightUOM && formData?.weightUnits !== weightUOM) {
          updates.push(["weightUnits", weightUOM]);
        }
        if ((formData?.totalQuantity ?? 0) !== nextTotalQty) {
          updates.push(["totalQuantity", nextTotalQty]);
        }
        if (updates.length === 0) return;
        syncingFormRef.current = true;
        updates.forEach(([field, value]) => form.change(field, value));
        syncingFormRef.current = false;
      },
      { values: true },
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.id, chargeRules]);

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => {
        const billingAccount = account;
        const autoCharges = getAutoCharges(formData, chargeRules);
        return (
          <Grid container spacing={0}>
            <Grid item xs={12} className={classes.arrayInputHeader}>
              <Typography variant="overline" color="textSecondary">
                OUTPUT INVENTORY:
              </Typography>
              <CustomFieldsButton
                form={form}
                accountFields={billingAccount?.inventoryCustomFields}
                selectedCustomFields={selectedFields}
                setSelectedFields={setSelectedFields}
              />
            </Grid>
            <Grid item xs={12}>
              <ArrayInput
                source="inventories.items"
                label={false}
                className={classes.arrayInput}
              >
                <SimpleFormIterator
                  addButton={
                    <AddCatalogUploadButtons
                      backPath={backPath}
                      form={form}
                      formData={formData}
                      account={account}
                      yardCommodity={yardCommodity}
                    />
                  }
                  removeButton={
                    <RemoveCopyButtons
                      form={form}
                      setWeightError={setWeightError}
                      yardCommodity={yardCommodity}
                    />
                  }
                >
                  <FormDataConsumer>
                    {({ scopedFormData, getSource, ...rest }) =>
                      scopedFormData && (
                        <Grid container direction="row" spacing={1}>
                          <Grid item xs={12} md={5}>
                            <TextInput
                              source={getSource("productName")}
                              label="Product Name"
                              variant="outlined"
                              fullWidth
                              validate={validateRequired}
                              helperText={false}
                              InputProps={{
                                endAdornment: getInfoBubble(
                                  formData,
                                  scopedFormData,
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <NumberInput
                              source={getSource("quantityAdvised")}
                              label="Qty Advised"
                              variant="outlined"
                              fullWidth
                              min="0"
                              validate={validateQuantity}
                              onChange={() => handleInventoriesQty(form)}
                              helperText={false}
                              InputProps={{ endAdornment: formData.totalUOM }}
                            />
                          </Grid>
                          {isInventoryFieldVisible(
                            InventoryField.PCS,
                            scopedFormData?.reloadYardCommodityId,
                          ) && (
                            <Grid item xs={12} md={3}>
                              <NumberInput
                                source={getSource("pcs")}
                                label="Pieces / Unit"
                                variant="outlined"
                                fullWidth
                                min="1"
                                placeholder={scopedFormData?.product?.pcs}
                                InputLabelProps={{
                                  shrink:
                                    scopedFormData?.pcs != null ||
                                    scopedFormData?.product?.pcs != null,
                                }}
                                validate={validatePieces}
                                helperText={false}
                              />
                            </Grid>
                          )}
                          {showWeight(account, scopedFormData) && (
                            <Grid item xs={12} md={3}>
                              <NumberInput
                                source={getSource("weight")}
                                label="Weight"
                                variant="outlined"
                                fullWidth
                                min="0"
                                validate={validateQuantity}
                                onChange={() =>
                                  handleInventoriesWeight(form, setWeightError)
                                }
                                helperText={false}
                                InputProps={{
                                  endAdornment: `${getWeightUOM(
                                    account,
                                    scopedFormData,
                                  )}/${formData.totalUOM}`,
                                }}
                              />
                            </Grid>
                          )}
                          <CustomFieldsTallySheet
                            {...props}
                            getSource={getSource}
                            recordFields={scopedFormData?.customFields}
                            accountFields={selectedFields}
                            header={false}
                            width={3}
                            disabled={false}
                          />
                        </Grid>
                      )
                    }
                  </FormDataConsumer>
                </SimpleFormIterator>
              </ArrayInput>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary">
                Total Load
              </Typography>
              <Grid container direction="row" spacing={1}>
                <Grid item xs={6} md={3}>
                  <NumberInput
                    source="totalQuantity"
                    label="Total Quantity"
                    variant="outlined"
                    fullWidth
                    min="0"
                    initialValue={0}
                    disabled
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <SelectInput
                    source="totalUOM"
                    label="Inventory UOM"
                    variant="outlined"
                    fullWidth
                    choices={getCommodityUnits(yardCommodity)}
                    optionText={unitOptionRenderer}
                    initialValue="UNIT"
                    validate={validateRequired}
                    onChange={() => handleUnitChange(form)}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <NumberInput
                    source="netWeight"
                    label="Est. Net Weight"
                    variant="outlined"
                    fullWidth
                    disabled
                    helperText={weightError ? weightError : false}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      endAdornment:
                        formData?.scaleWeightUnits ||
                        formData?.weightUnits ||
                        "",
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {!formData?.skipInvoice && isAuthorized(AclType.INVOICES) && (
              <Grid item xs={12}>
                <fieldset>
                  <legend>
                    <Typography variant="overline" color="textSecondary">
                      Billing <NewBadge />
                    </Typography>
                  </legend>
                  <Grid container spacing={8}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="overline" color="textSecondary">
                        Auto Charges
                      </Typography>
                      <ArrayField
                        record={autoCharges}
                        source="items"
                        label={false}
                        fullWidth
                      >
                        <Datagrid>
                          <FunctionField
                            label="Service"
                            render={(record) => {
                              const parts =
                                record?.serviceNote?.split(" - ") || [];
                              return parts[0] || "";
                            }}
                          />
                          <FunctionField
                            label="Quantity"
                            render={(record) => {
                              const qty = Number(record?.quantity || 0);
                              const units = record?.units || "";
                              return qty ? `${qty} ${getUnitName(units)}` : "";
                            }}
                          />
                          <FunctionField
                            label="Est. Charge"
                            render={(record) => {
                              const amount = Number(record?.amount || 0);
                              return amount ? `$${amount.toFixed(2)}` : "";
                            }}
                          />
                        </Datagrid>
                      </ArrayField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="overline" color="textSecondary">
                        Manual Charges
                      </Typography>
                      <SmartTipIcon
                        label="manual-charges"
                        alignRight={false}
                        mt={20}
                      />
                      <ArrayInput
                        source="manualCharges.items"
                        label={false}
                        fullWidth
                        disabled={!chargeRules?.length}
                      >
                        <SimpleFormIterator disableReordering={true}>
                          <FormDataConsumer>
                            {({ getSource, scopedFormData }) => {
                              const applicableRules =
                                filterApplicableManualRules(
                                  formData,
                                  chargeRules,
                                  ChargeEvent.INBOUND,
                                );
                              const selectedRule = applicableRules.find(
                                (rule) =>
                                  rule.id === scopedFormData?.chargeRuleId,
                              );
                              const billingUOM = getUnitName(
                                selectedRule?.billingUOM,
                              );
                              return (
                                <Grid container direction="row" spacing={1}>
                                  <Grid item xs={7}>
                                    <SelectInput
                                      source={getSource("chargeRuleId")}
                                      label="Service"
                                      variant="outlined"
                                      fullWidth
                                      helperText={false}
                                      choices={applicableRules.map((rule) => ({
                                        id: rule.id,
                                        name: getManualRuleLabel(rule),
                                      }))}
                                      onChange={(value) =>
                                        applyManualRuleDefaultQuantity({
                                          value,
                                          applicableRules,
                                          formData,
                                          scopedFormData,
                                          form,
                                          getSource,
                                        })
                                      }
                                    />
                                  </Grid>
                                  <Grid item xs={5}>
                                    <NumberInput
                                      source={getSource("quantity")}
                                      label="Quantity"
                                      variant="outlined"
                                      fullWidth
                                      min="0"
                                      validate={validateQuantity}
                                      helperText={getManualChargeAmount(
                                        selectedRule,
                                        scopedFormData,
                                      )}
                                      InputProps={{
                                        endAdornment: billingUOM,
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              );
                            }}
                          </FormDataConsumer>
                        </SimpleFormIterator>
                      </ArrayInput>
                    </Grid>
                  </Grid>
                </fieldset>
              </Grid>
            )}
          </Grid>
        );
      }}
    </FormDataConsumer>
  );
};

const InputInventoryItemExpand = ({ inputInventoryItems, record }) => {
  const classes = useStyles();
  const itemIndex = inputInventoryItems.findIndex(
    (item) => (item.id || item.locationFullName) === record.id,
  );
  const sourcePrefix =
    itemIndex >= 0 ? `inputInventoryItems.items[${itemIndex}]` : "";
  const quantityAvailable =
    Number(record?.quantityOnHand || 0) -
    Number(record?.quantityCommitted || 0);

  if (!sourcePrefix) return null;

  return (
    <Box className={classes.inputInventoryItemExpand}>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={4}>
          <TextInput
            source={`${sourcePrefix}.locationFullName`}
            label="Location"
            variant="outlined"
            fullWidth
            disabled
            helperText={false}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput
            source={`${sourcePrefix}.quantityOnHand`}
            label="Qty. On Hand"
            variant="outlined"
            fullWidth
            disabled
            helperText={false}
            InputProps={{
              endAdornment: getUnitName(record?.inventory?.inventoryUOM),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <NumberInput
            source={`${sourcePrefix}.quantityToProcess`}
            label="Qty. To Process"
            variant="outlined"
            fullWidth
            min="0"
            max={quantityAvailable}
            validate={[number(), minValue(0)]}
            helperText={false}
            InputProps={{
              endAdornment: getUnitName(record?.inventory?.inventoryUOM),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const InputInventoryItemsPickList = ({
  inputInventory,
  inputInventoryItems,
}) => {
  const listItems = useMemo(
    () =>
      inputInventoryItems.map((item, index) => ({
        ...item,
        id: item.id || item.locationFullName || `input-inventory-item-${index}`,
        inventory: inputInventory,
        quantityAvailable:
          Number(item.quantityOnHand || 0) -
          Number(item.quantityCommitted || 0),
      })),
    [inputInventory, inputInventoryItems],
  );
  const listIds = useMemo(() => listItems.map(({ id }) => id), [listItems]);
  const listContext = useList({
    data: listItems,
    ids: listIds,
    loaded: true,
    sort: { field: "locationFullName", order: "ASC" },
  });

  return (
    <ListContextProvider value={listContext}>
      <CustomizableDatagrid
        label="PICK LIST:"
        hasBulkActions={false}
        rowClick="expand"
        expand={<InputInventoryItemExpand inputInventoryItems={listItems} />}
        storageProp="WorkOrderInputInventoryItems_large"
        defaultColumns={[
          "inventory.productName",
          "quantityCommitted",
          "quantityAvailable",
          "inventory.inventoryUOM",
          "status",
        ]}
      >
        {[
          <FunctionField
            source="inventory.productName"
            label="Product Name"
            sortable={true}
            render={(record) => record?.inventory?.productName || ""}
          />,
          <NumberField source="quantityCommitted" label="Qty. Committed" />,
          <FunctionField
            source="quantityAvailable"
            label="Qty. Available"
            sortable={false}
            render={(record) => record?.quantityAvailable ?? 0}
          />,
          <TextField source="inventory.inventoryUOM" label="UOM" />,
          <TextField source="status" label="Status" />,
        ]}
      </CustomizableDatagrid>
    </ListContextProvider>
  );
};

const WorkOrderDefaultLocationFields = ({ accountId, requireDefaultZone }) => {
  const form = useForm();
  const notify = useNotify();
  const [areas, setAreas] = useState(
    window.appUser?.reloadYard?.areas?.items?.filter(
      (area) => area.active === "true",
    ) || [],
  );
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);

  useEffect(() => {
    const fetchAreas = async () => {
      setIsLoadingAreas(true);
      try {
        const response = await API.graphql(
          graphqlOperation(areaByActiveBySortKey, {
            reloadYardId: window.appUser?.reloadYardId,
            activeSortKey: { beginsWith: { active: "true" } },
            limit: 1000,
          }),
        );
        const activeAreas = response.data.areaByActiveBySortKey.items || [];
        window.appUser.reloadYard.areas.items = activeAreas;
        setupLocationMaps(window.appUser.reloadYard);
        setAreas(activeAreas);
      } catch (error) {
        logError(
          "workOrders:WorkOrderCreate:defaultLocationFields",
          error,
          notify,
        );
      } finally {
        setIsLoadingAreas(false);
      }
    };

    fetchAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormDataConsumer>
      {({ formData }) => {
        const yardCommodity = getYardCommodity(formData?.reloadYardCommodityId);
        const areaChoices = filterAreas(areas, yardCommodity);
        const selectedArea = areaChoices.find(
          (area) => area.id === formData?.areaId,
        );
        const zoneChoices = selectedArea
          ? filterZones(
              selectedArea.zones?.items || [],
              formData?.reloadYardCommodityId,
              accountId,
            )
          : [];

        return (
          <>
            <Grid item xs={12} md={4}>
              <SelectInput
                source="areaId"
                label="Default Area"
                disabled={!formData?.reloadYardCommodityId || isLoadingAreas}
                choices={areaChoices}
                onChange={(event) => {
                  const nextArea = areaChoices.find(
                    (area) => area.id === event.target.value,
                  );
                  const nextZones = nextArea
                    ? filterZones(
                        nextArea.zones?.items || [],
                        formData?.reloadYardCommodityId,
                        accountId,
                      )
                    : [];
                  form.change(
                    "zoneId",
                    nextZones.length === 1 ? nextZones[0].id : "",
                  );
                }}
                validate={validateRequired}
                variant="outlined"
                fullWidth
                helperText={false}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SelectInput
                source="zoneId"
                label="Default Zone"
                disabled={!formData?.areaId}
                choices={zoneChoices}
                allowEmpty={!requireDefaultZone}
                validate={requireDefaultZone ? validateRequired : undefined}
                variant="outlined"
                fullWidth
                helperText={false}
              />
            </Grid>
          </>
        );
      }}
    </FormDataConsumer>
  );
};

const WorkOrderCreateForm = ({
  inputInventory,
  inputInventoryItem,
  backPath,
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const [inbound, setInbound] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const yardCommodities =
    window.appUser?.reloadYard?.activeYardCommodities?.items || [];

  const inboundShipmentId =
    inputInventoryItem?.inboundShipmentId ||
    inputInventoryItem?.inventory?.inboundShipmentId;

  useEffect(() => {
    const fetchInbound = async () => {
      setInbound(undefined);
      if (!inboundShipmentId) return;

      setIsLoading(true);
      try {
        const response = await API.graphql(
          graphqlOperation(getInboundShipment, { id: inboundShipmentId }),
        );
        setInbound(response.data.getInboundShipment);
      } catch (err) {
        logError("workOrders:WorkOrderCreate:fetchInbound", err, notify);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInbound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboundShipmentId]);

  const selectedYardCommodity = getYardCommodity(
    inputInventoryItem?.inventory?.reloadYardCommodityId ||
      inbound?.reloadYardCommodityId,
  );

  if (!inputInventoryItem) {
    return (
      <Typography color="error">
        No input inventory item was provided for this work order.
      </Typography>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" p={2}>
        <CircularProgress size={24} />
        <Box ml={2}>
          <Typography>Loading inbound shipment...</Typography>
        </Box>
      </Box>
    );
  }

  if (!inbound) {
    return (
      <Typography color="error">
        No inbound shipment was found for this inventory item.
      </Typography>
    );
  }

  const inputInventoryItems =
    inputInventory?.inventoryItems?.items?.length > 0
      ? inputInventory.inventoryItems.items
      : inputInventoryItem
        ? [inputInventoryItem]
        : [];
  const initialValues = {
    accountName: inbound.accountName,
    assignedTo: inbound.assignedTo,
    inboundPoNumber: inbound.poNumber,
    inputInventory: {
      productName: inputInventoryItem?.inventory?.productName,
      quantityOnHand: inputInventoryItem?.quantityOnHand || 0,
      loadType: getLoadTypeLabel(
        inputInventoryItem?.inventory?.reloadYardCommodityId,
      ),
      locationFullName: inputInventoryItem?.locationFullName || "Not assigned",
    },
    inputInventoryItems: {
      items: inputInventoryItems.map((item) => ({
        ...item,
        inventory: item.inventory || inputInventory,
        quantityToProcess: item.quantityToProcess || 0,
      })),
    },
    inventories: {
      items: [],
    },
    account: inbound.account,
    reloadYardCommodityId:
      inputInventoryItem?.inventory?.reloadYardCommodityId ||
      inbound.reloadYardCommodityId,
    areaId:
      inbound.areaId ||
      inputInventoryItem?.areaId ||
      inputInventoryItem?.inventory?.areaId,
    zoneId:
      inbound.zoneId ||
      inputInventoryItem?.zoneId ||
      inputInventoryItem?.inventory?.zoneId,
    skipInvoice: inbound.skipInvoice,
    totalUOM:
      inputInventoryItem?.inventory?.inventoryUOM || inbound.totalUOM || "UNIT",
    totalQuantity: 0,
    netWeight: null,
    weightUnits: getWeightUOM(inbound.account, null),
  };

  return (
    <TabbedForm
      variant="outlined"
      initialValues={initialValues}
      submitOnEnter={false}
      toolbar={
        <WorkOrderCreateToolbar
          inbound={inbound}
          inputInventoryItem={inputInventoryItem}
          backPath={backPath}
        />
      }
    >
      <FormTab path="" label="Input Inventory">
        <Grid container spacing={1} className={classes.formGrid}>
          <Grid item xs={12} md={4}>
            <TextInput
              source="accountName"
              label="Account"
              variant="outlined"
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput
              source="inboundPoNumber"
              label="Inbound PO"
              variant="outlined"
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AutocompleteInput
              source="reloadYardCommodityId"
              label="Load Type"
              choices={yardCommodities}
              optionText={(choice) =>
                `${choice.commodity?.name || ""} - ${choice.units || ""}`
              }
              optionValue="id"
              validate={validateRequired}
              variant="outlined"
              fullWidth
              helperText={false}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput
              source="assignedTo"
              label="Assigned To"
              variant="outlined"
              fullWidth
            />
          </Grid>
          <WorkOrderDefaultLocationFields
            accountId={inbound.accountId}
            requireDefaultZone={inbound.account?.requireDefaultZone}
          />
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">
              Inventory Items
            </Typography>
          </Grid>
          {inputInventoryItems.length > 0 && (
            <Grid item xs={12}>
              <InputInventoryItemsPickList
                inputInventory={inputInventory}
                inputInventoryItems={inputInventoryItems}
              />
            </Grid>
          )}
          {inputInventoryItems.length === 0 && (
            <Grid item xs={12}>
              <Typography color="textSecondary" variant="body2">
                No inventory items found.
              </Typography>
            </Grid>
          )}
        </Grid>
      </FormTab>
      <FormTab path="tallySheet" label="Output Inventory">
        <TallySheetTab
          backPath={backPath}
          account={inbound.account}
          billingAccountId={inbound.billToAccountId || inbound.accountId}
          yardCommodity={selectedYardCommodity}
        />
      </FormTab>
    </TabbedForm>
  );
};

export const WorkOrderCreate = (props) => {
  const [inventory] = useState(props.location?.state?.inventory);
  const [inventoryItem] = useState(
    props.location?.state?.inventoryItem ||
      props.location?.state?.inputInventoryItem,
  );
  const backPath = getBackPath(props);
  const inputInventoryItem =
    inventoryItem && inventory
      ? { ...inventoryItem, inventory }
      : inventoryItem || getDefaultInventoryItem(inventory);
  const inputInventory = inventory || inputInventoryItem?.inventory;

  return (
    <Create
      {...props}
      title={<ForceTitle title="New Work Order" />}
      actions={<WorkOrderCreateActions {...props} backPath={backPath} />}
      aside={
        <WorkAside
          {...props}
          backPath={backPath}
          inventory={inputInventory}
          inventoryItems={
            inputInventory?.inventoryItems?.items ||
            (inputInventoryItem ? [inputInventoryItem] : [])
          }
        />
      }
    >
      <WorkOrderCreateForm
        inputInventory={inputInventory}
        inputInventoryItem={inputInventoryItem}
        backPath={backPath}
      />
    </Create>
  );
};
